import { GridColDef } from '@mui/x-data-grid';
import { usePermissions } from '../../../context/PermissionContext';
import PermissionIconButton from './PermissionIconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ActionColumnConfig {
  module: string;
  onEdit?: (row: any) => void;
  onDelete?: (id: string) => void;
  onBlock?: (id: string) => void;
  onUnblock?: (id: string) => void;
  editField?: string;
  deleteField?: string;
  statusField?: string;
  hideDeleteCondition?: (row: any) => boolean;
}

export const usePermissionBasedColumns = () => {
  const { hasPermission } = usePermissions();

  const getActionColumns = (config: ActionColumnConfig): GridColDef[] => {
    const columns: GridColDef[] = [];

    // Add Edit column if user has permission
    if (hasPermission(config.module, 'Edit')) {
      columns.push({
        field: config.editField || 'edit',
        headerName: 'Edit',
        flex: 0.5,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => (
          <PermissionIconButton
            module={config.module}
            action="Edit"
            onClick={() => config.onEdit?.(params.row)}
            icon={<EditIcon fontSize="small" />}
            tooltip="Edit"
          />
        ),
      });
    }

    // Add Status column if user has Block or Unblock permission
    if (hasPermission(config.module, 'Block') || hasPermission(config.module, 'Unblock')) {
      columns.push({
        field: config.statusField || 'status',
        headerName: 'Status',
        flex: 0.5,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => {
          const isBlocked = params.value === 'blocked';
          const id = params.row.id;
          const validId = typeof id === 'string' ? id : '';
          const action = isBlocked ? 'Unblock' : 'Block';

          // Check if user has permission for the specific action
          if (!hasPermission(config.module, action)) {
            return null;
          }

          return (
            <PermissionIconButton
              module={config.module}
              action={action}
              onClick={() =>
                isBlocked
                  ? config.onUnblock?.(validId)
                  : config.onBlock?.(validId)
              }
              icon={
                isBlocked ? (
                  <BlockIcon fontSize="small" />
                ) : (
                  <CheckCircleIcon fontSize="small" />
                )
              }
              color={isBlocked ? 'error' : 'success'}
              tooltip={isBlocked ? 'Unblock' : 'Block'}
            />
          );
        },
      });
    }

    // Add Delete column if user has permission
    if (hasPermission(config.module, 'Delete')) {
      columns.push({
        field: config.deleteField || 'delete',
        headerName: 'Delete',
        flex: 0.5,
        filterable: false,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => {
          // Check if delete button should be hidden based on condition
          if (config.hideDeleteCondition && config.hideDeleteCondition(params.row)) {
            return null;
          }
          
          return (
            <PermissionIconButton
              module={config.module}
              action="Delete"
              onClick={() => config.onDelete?.(params.row.id)}
              icon={<DeleteIcon />}
              color="error"
              tooltip="Delete"
            />
          );
        },
      });
    }

    return columns;
  };

  return { getActionColumns };
}; 